import { queryGraphQL } from 'common/util/query';
import { addApplicationMessage, addApplicationMessages } from 'common/actions/applicationMessagesActions';
import { node, nodeWithArgs, toQuery } from 'common/util/queryHelper';
import { UPDATE_STUDY } from '../actions/study';

export const TASK_COUNT_LOADED = 'TASK_COUNT_LOADED ';
export const TASKS_LOADING = 'TASKS_LOADING';
export const TASKS_UNLOADED = 'TASKS_UNLOADED ';
export const SDFA_TASKS_LOADED = 'SDFA_TASKS_LOADED ';
export const DOA_TASKS_LOADED = 'DOA_TASKS_LOADED';
export const EVENT_TASKS_LOADED = 'EVENT_TASKS_LOADED';

export function getTasks(studyBinderId, studyId, status) {
    return async (dispatch) => {
        await dispatch(queryingTasks(true));

        if (status === 'pending') {
            await dispatch(getSDFATasks(studyBinderId, studyId, status));
            await dispatch(getStudyDOATasks(studyBinderId, studyId, status));
            await dispatch(getPendingBinderEventSignatures(studyBinderId));
        }

        if (status === 'completed') {
            await dispatch(getSDFATasks(studyBinderId, studyId, status));
            await dispatch(getStudyDOATasks(studyBinderId, studyId, status));
            await dispatch(getCompleteBinderEventSignatures(studyBinderId));
        }

        await dispatch(queryingTasks(false));
    };
}

export function getPendingStudyTasksCount() {
    return async (dispatch, getState) => {
        const storeState = getState();
        const { id: studyId } = storeState.study;
        const query = nodeWithArgs(
            'study',
            {
                studyId,
            },
            ['pendingTasksCount']
        );
        const response = await queryGraphQL(toQuery(query));

        if (response && response.errors) {
            dispatch(addApplicationMessage('error', 'Failed to fetch study pending tasks count.'));
        } else {
            const studyData = response.data.study;
            dispatch({
                type: UPDATE_STUDY,
                payload: studyData,
            });
        }
    };
}

export function getTaskCount(studyBinderId, studyId, status) {
    const count = nodeWithArgs(
        'studyWorkflowCountByProcessVariable',
        {
            studyId,
            processVariableName: 'binderId',
            processVariableValue: studyBinderId,
            status,
        },
        ['count']
    );

    return (dispatch) => {
        return queryGraphQL(toQuery(count)).then((results) => {
            if (results.errors) {
                dispatch(addApplicationMessages('error', results.formattedErrors));
            } else {
                dispatch(taskCountLoaded(status, results.data.studyWorkflowCountByProcessVariable.count));
            }
        });
    };
}

export function getStudyDOATasks(studyBinderId, studyId, status) {
    return (dispatch) => {
        const tasks = nodeWithArgs(
            'tasksOfDOAForStudy',
            {
                studyId,
                status: status === 'completed' ? 'complete' : 'pending',
            },
            [
                'id',
                'status',
                node('requestedFrom', ['id', 'displayName', 'emailAddress']),
                node('createdBy', ['id', 'displayName', 'emailAddress']),
                node('delegation', [
                    'id',
                    node('user', ['id', 'displayName', 'emailAddress']),
                    node('rolesManagement', ['id', 'name']),
                    'taskIds',
                    'startDate',
                    'endDate',
                ]),
                'sentDate',
                'signedDate',
            ]
        );

        return queryGraphQL(toQuery(tasks)).then((results) => {
            if (results.errors) {
                dispatch(addApplicationMessages('error', results.formattedErrors));
            } else {
                dispatch(doaTasksLoaded(status, results.data.tasksOfDOAForStudy));
            }
        });
    };
}

export function getPendingBinderEventSignatures(studyBinderId) {
    return (dispatch) => {
        const query = nodeWithArgs('binder', {
            id: studyBinderId
        }, [
            'id',
            node('pendingEventSignatures', [
                'id',
                'status',
                node('requestedFrom', [
                    'id',
                    'displayName',
                    'emailAddress',
                ]),
                node('createdBy', [
                    'id',
                    'displayName',
                    'emailAddress',
                ]),
                node('event', [
                    'id',
                    'eventType',
                ]),
                'sentDate',
                'binderName',
            ])
        ]);

        return queryGraphQL(toQuery(query)).then((response) => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.formattedErrors));
            } else {
                dispatch(eventTasksLoaded('pending', response.data.binder.pendingEventSignatures));
            }
        });
    };
}

export function getCompleteBinderEventSignatures(studyBinderId) {
    return (dispatch) => {
        const query = nodeWithArgs('binder', {
            id: studyBinderId
        }, [
            'id',
            node('completeEventSignatures', [
                'id',
                'status',
                node('requestedFrom', [
                    'id',
                    'displayName',
                    'emailAddress',
                ]),
                node('createdBy', [
                    'id',
                    'displayName',
                    'emailAddress',
                ]),
                node('event', [
                    'id',
                    'eventType',
                ]),
                'sentDate',
                'signedDate',
                'binderName',
            ])
        ]);

        return queryGraphQL(toQuery(query)).then((response) => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.formattedErrors));
            } else {
                dispatch(eventTasksLoaded('completed', response.data.binder.completeEventSignatures));
            }
        });
    };
}

export function getSDFATasks(studyBinderId, studyId, status, limit = '100', offset = '0') {
    const tasks = nodeWithArgs(
        'studyWorkflowsByProcessVariable',
        {
            studyId,
            processVariableName: 'binderId',
            processVariableValue: studyBinderId,
            status,
            maxResults: limit,
            offset,
        },
        [
            'id',
            'title',
            'author',
            'authorDisplayName',
            'sentDate',
            'endDate',
            'status',
            'totalInstances',
            'activeInstances',
            'completedInstances',
            node('document', ['name', node('documentType', ['displayName', 'id'])]),
        ]
    );

    return (dispatch) => {
        return queryGraphQL(toQuery(tasks)).then((results) => {
            if (results.errors) {
                dispatch(addApplicationMessages('error', results.formattedErrors));
            } else {
                dispatch(sdfaTasksLoaded(status, results.data.studyWorkflowsByProcessVariable));
            }
        });
    };
}

export function unloadTasks() {
    return (dispatch) => {
        dispatch(tasksUnloaded);
    };
}

export function queryingTasks(state = true) {
    return {
        type: TASKS_LOADING,
        payload: state,
    };
}

export function taskCountLoaded(status, count) {
    return {
        type: TASK_COUNT_LOADED,
        payload: { status, count },
    };
}

export function sdfaTasksLoaded(status, tasks) {
    return {
        type: SDFA_TASKS_LOADED,
        payload: { status, tasks },
    };
}

export function doaTasksLoaded(status, tasks) {
    return {
        type: DOA_TASKS_LOADED,
        payload: { status, tasks },
    };
}

export function eventTasksLoaded(status, tasks) {
    return {
        type: EVENT_TASKS_LOADED,
        payload: { status, tasks }
    };
}

export function tasksUnloaded() {
    return {
        type: TASKS_UNLOADED,
    };
}
